// The fictional basemap of a demo state (GET /api/safety/basemap), drawn on one
// canvas like a light street map: land cover, water, rivers, roads by class and
// town labels by zoom. It replaces the real tile layers for demo states, whose
// data sit at synthetic coordinates where real tiles would show the wrong place.
import L from 'leaflet';
import { createLayerComponent } from '@react-leaflet/core';
import { authHeaders } from '../../utils/authHeader';

const PANE = 'demoBasemap';
export const DEMO_LAND = '#f2efe9';
const STYLE = {
  land: { forest: '#d4e6c8', urban: '#e8e3de', farm: '#eef0d9' },
  water: '#aad3df',
  roads: {             // [casing, fill, width at z13, min zoom]
    major: ['#d99a5c', '#fcd6a4', 6, 7],
    secondary: ['#c2b98a', '#f7fabf', 5, 9],
    tertiary: ['#bdb6ae', '#ffffff', 4, 10],
    minor: ['#cfc9c2', '#ffffff', 2.6, 12],
  },
  places: { city: [9, 15], town: [9, 13], village: [11, 12], hamlet: [13, 11] },   // [min zoom, font px]
};

const cache = new Map();
/** The basemap of `state`, fetched once per session. */
export function loadBasemap(state) {
  if (!cache.has(state)) {
    cache.set(state, (async () => {
      const res = await fetch(`/api/safety/basemap?state=${encodeURIComponent(state)}`, { headers: await authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.success === false) throw new Error(body.error || `basemap failed (${res.status})`);
      return body;
    })().catch((e) => { cache.delete(state); throw e; }));
  }
  return cache.get(state);
}

const CanvasBasemap = L.Layer.extend({
  initialize(options) {
    L.setOptions(this, options);
    this._features = [];
  },
  onAdd(map) {
    let pane = map.getPane(PANE);
    if (!pane) {
      pane = map.createPane(PANE);
      pane.style.zIndex = 200;           // where tile layers sit
      pane.style.pointerEvents = 'none';
    }
    this._canvas = L.DomUtil.create('canvas', 'leaflet-zoom-hide', pane);
    this._prevBg = map.getContainer().style.background;
    map.getContainer().style.background = DEMO_LAND;
    map.on('moveend zoomend resize', this._redraw, this);
    if (map.attributionControl && this.options.attribution) map.attributionControl.addAttribution(this.options.attribution);
    loadBasemap(this.options.state).then((b) => { this._features = b.features || []; this._redraw(); })
      .catch(() => { /* the map works without its background */ });
  },
  onRemove(map) {
    map.off('moveend zoomend resize', this._redraw, this);
    map.getContainer().style.background = this._prevBg || '';
    if (map.attributionControl && this.options.attribution) map.attributionControl.removeAttribution(this.options.attribution);
    if (this._canvas) L.DomUtil.remove(this._canvas);
    this._canvas = null;
  },
  getAttribution() { return this.options.attribution; },
  _redraw() {
    const map = this._map;
    const canvas = this._canvas;
    if (!map || !canvas) return;
    const size = map.getSize();
    const ratio = window.devicePixelRatio || 1;
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    canvas.width = size.x * ratio;
    canvas.height = size.y * ratio;
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, size.x, size.y);
    const z = map.getZoom();
    const view = map.getBounds().pad(0.1);
    const s = view.getSouth(); const n = view.getNorth(); const w = view.getWest(); const e = view.getEast();
    const inView = (b) => b && b[2] >= s && b[0] <= n && b[3] >= w && b[1] <= e;
    const px = (p) => map.latLngToContainerPoint([p[0], p[1]]);
    const path = (pts) => {
      let last = null;
      pts.forEach((p, i) => {
        const q = px(p);
        if (i === 0) ctx.moveTo(q.x, q.y);
        else if (!last || Math.abs(q.x - last.x) + Math.abs(q.y - last.y) > 1.2 || i === pts.length - 1) ctx.lineTo(q.x, q.y);
        else return;
        last = q;
      });
    };
    const scale = 2 ** (z - 13);
    const byLayer = { land: [], water: [], river: [], road: [], place: [] };
    this._features.forEach((f) => { if (inView(f.b) && byLayer[f.l]) byLayer[f.l].push(f); });

    byLayer.land.forEach((f) => {
      ctx.beginPath();
      f.g.forEach((ring) => { path(ring); ctx.closePath(); });
      ctx.fillStyle = STYLE.land[f.k] || STYLE.land.farm;
      ctx.fill('evenodd');
    });
    ctx.fillStyle = STYLE.water;
    byLayer.water.forEach((f) => {
      ctx.beginPath();
      f.g.forEach((ring) => { path(ring); ctx.closePath(); });
      ctx.fill('evenodd');
    });
    ctx.strokeStyle = STYLE.water;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(1, Math.min(6, 2.5 * scale));
    byLayer.river.forEach((f) => { ctx.beginPath(); path(f.g); ctx.stroke(); });

    const order = ['minor', 'tertiary', 'secondary', 'major'];
    const shown = byLayer.road.filter((f) => STYLE.roads[f.k] && z >= STYLE.roads[f.k][3]);
    [0, 1].forEach((pass) => order.forEach((k) => {           // all casings first, then all fills
      const [casing, fill, width] = STYLE.roads[k];
      const wpx = Math.max(0.8, Math.min(width * 2.2, width * scale));
      ctx.strokeStyle = pass === 0 ? casing : fill;
      ctx.lineWidth = pass === 0 ? wpx + 1.6 : wpx;
      shown.forEach((f) => { if (f.k === k) { ctx.beginPath(); path(f.g); ctx.stroke(); } });
    }));

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const placed = [];
    byLayer.place
      .filter((f) => STYLE.places[f.k] && z >= STYLE.places[f.k][0])
      .sort((a, b) => STYLE.places[b.k][1] - STYLE.places[a.k][1])
      .forEach((f) => {
        const q = px(f.g);
        const font = STYLE.places[f.k][1];
        ctx.font = `${f.k === 'city' || f.k === 'town' ? 600 : 500} ${font}px system-ui, "Segoe UI", sans-serif`;
        const wText = ctx.measureText(f.n).width;
        const box = [q.x - wText / 2 - 3, q.y - font / 2 - 2, q.x + wText / 2 + 3, q.y + font / 2 + 2];
        if (placed.some((o) => box[0] < o[2] && box[2] > o[0] && box[1] < o[3] && box[3] > o[1])) return;
        placed.push(box);
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(255,255,255,0.9)';
        ctx.strokeText(f.n, q.x, q.y);
        ctx.fillStyle = '#4a4540';
        ctx.fillText(f.n, q.x, q.y);
      });
  },
});

const DemoBasemap = createLayerComponent(
  ({ state, ...options }, context) => ({ instance: new CanvasBasemap({ ...options, state, attribution: '© OpenStreetMap contributors' }), context }),
  () => {},
);

export default DemoBasemap;
